if (!customElements.get('component-cart-item')) {
  customElements.define('component-cart-item', class CartItem extends HTMLElement {
    constructor() {
      super();

      this.itemImage = this.querySelector('.js-cart-item-image');
      this.minusButton = this.querySelector('.js-cart-item-button-minus');
      this.plusButton = this.querySelector('.js-cart-item-button-plus');
      this.quantity = this.querySelector('.js-cart-item-quantity');
      this.price = this.querySelector('.js-cart-item-price');
      this.removeButton = this.querySelector('.js-cart-item-button-remove');
      this.linkToProduct = this.querySelectorAll('.js-cart-item-link');
      this.errorMessageSoldOutEl = this.querySelector('.js-error-message-sold-out');
      this.errorMessageLastItemEl = this.querySelector('.js-error-message-last-item');
      this.checkoutButton = document.querySelector('.js-checkout-button');

      this.settings = {
        countryCode: window.Shopify.country,
        itemKey: this.dataset.itemKey,
        itemSellingPlanId: this.dataset.itemSellingPlanId !== 'null' ? this.dataset.itemSellingPlanId : null,
        bundleId: this.dataset.bundleId,
        isGWP: this.dataset.isGwp === 'true'
      };

      // Disable minus and plus buttons if item is a free gift
      if (this.settings.isGWP) {
        this.minusButton.disabled = true;
        this.plusButton.disabled = true;
      }

      this.setEventListeners();
    }

    setEventListeners() {
      this.linkToProduct.forEach(link => {
        link.addEventListener('click', async () => {
          window.sendGTMData && window.sendGTMData({
            event: 'cart_item_click',
            link_url: link.href
          });
        });
      });

      this.plusButton?.addEventListener('click', async () => {
        let updatedItem;

        try {
          const cart = window.cartManager.getLocalCart();

          this.plusButton.disabled = true;
          this.minusButton.disabled = true;
          this.removeButton.disabled = true;
          this.checkoutButton.disabled = true;

          if (this.settings.bundleId) {
            updatedItem = await window.cartManager.increaseBundleQuantity(this.settings.bundleId);
          } else {
            const item = cart
              .items
              .find(item => {
                  const sellingPlanId = this.getSellingPlanId(item);
                  return item.key === this.settings.itemKey && !item.properties._bundleId && !item.properties._isGWP && sellingPlanId == this.settings.itemSellingPlanId;
                });

            if (!item) {
              throw 'Item not found';
            }

            if (item.properties._isGWP) {
              throw 'Can not add more than one gift';
            }

            updatedItem = await window.cartManager.increaseItemQuantity(item.key, this.getSellingPlanId(item));
          }

          if (!updatedItem) {
            throw 'Item not updated';
          }

          window.dispatchEvent(new CustomEvent('update-cart', {detail: {openCartDrawer: false}}));
        } catch (err) {
          console.error(err);
          if (err?.cause?.status === 422 && (err.cause.message?.includes('in your cart') || err.cause.message?.includes('is already sold out'))) {
            this.plusButton.disabled = true;
            if (err.cause.message.includes('is already sold out')) {
              this.minusButton.disabled = true;
              this.errorMessageSoldOutEl.classList.remove('hidden');
            } else if (err.cause.message.includes('in your cart')) {
              this.errorMessageLastItemEl.classList.remove('hidden');
              this.minusButton.disabled = false;
            }
          } else {
            // Any other failure is transient (a throttled 429, a stale line item key). Hand both
            // buttons back: `updatedItem` is always undefined in this catch, so the quantity check
            // this replaces never matched and left Plus disabled until the customer reloaded.
            // Only the 422 branch above describes a state where Plus should stay disabled.
            this.plusButton.disabled = false;
            this.minusButton.disabled = false;
          }
        } finally {
          this.removeButton.disabled = false;
          this.checkoutButton.disabled = false;
        }
      });

      this.minusButton?.addEventListener('click', async () => {
        try {
          const cart = window.cartManager.getLocalCart();
          let updatedItem;

          this.plusButton.disabled = true;
          this.minusButton.disabled = true;
          this.removeButton.disabled = true;
          this.checkoutButton.disabled = true;

          const item = cart
            .items
            .find(item => {
              if (this.settings.bundleId) {
                return item.properties._bundleId === this.settings.bundleId;
              } else {
                const sellingPlanId = this.getSellingPlanId(item);
                return item.key === this.settings.itemKey && !item.properties._bundleId && !item.properties._isGWP && sellingPlanId == this.settings.itemSellingPlanId;
              }
            });

          if (!item) {
            throw 'Item not found';
          }

          if (this.settings.bundleId) {
            updatedItem = await window.cartManager.decreaseBundleQuantity(this.settings.bundleId);
          } else {
            updatedItem = await window.cartManager.decreaseItemQuantity(item.key, this.getSellingPlanId(item));
          }

          if (!updatedItem) {
            throw 'Item not updated';
          }

          item.quantity -= 1;

          window.dispatchEvent(new CustomEvent('update-cart', {detail: {openCartDrawer: false}}));
        } catch (err) {
          console.error(err);
          if (err?.cause?.status === 422 && err.cause.message?.includes('is already sold out')) {
            this.minusButton.disabled = true;
            this.plusButton.disabled = true;
            this.errorMessageSoldOutEl.classList.remove('hidden');
          } else {
            this.minusButton.disabled = false;
            this.plusButton.disabled = false;
          }
        } finally {
          this.removeButton.disabled = false;
          this.checkoutButton.disabled = false;
        }
      });

      this.removeButton?.addEventListener('click', async () => {
        try {
          const cart = window.cartManager.getLocalCart();
          let newCart = null;

          this.removeButton.disabled = true;
          this.checkoutButton.disabled = true;

          if (this.settings.bundleId) {
            const items = cart.items
              .filter(item => item.properties._bundleId == this.settings.bundleId);
            window.broadcastChannel.dispatchEvent('disable-cart-upsells');
            newCart = await window.cartManager.removeBundle(this.settings.bundleId, items.length);

            if (!newCart) {
              throw 'Item was not removed';
            }
          } else {
            const item = cart
              .items
              .find(item => {
                const sellingPlanId = this.getSellingPlanId(item);
                return item.key === this.settings.itemKey && !item.properties._bundleId && sellingPlanId == this.settings.itemSellingPlanId
              });

            if (!item) {
              throw 'Item not found';
            }

            window.broadcastChannel.dispatchEvent('disable-cart-upsells');
            newCart = await window.cartManager.removeItem(item.key);

            if (!newCart) {
              throw 'Item was not removed';
            }

            if (item.properties._isGWP) {
              // Send GWP GTM data
              window.sendGTMData && window.sendGTMData({
                event: 'gwp_remove_from_cart',
                source: 'cart-drawer',
                product_id: this.settings.bundleId || item.id
              });
            }
          }

          window.dispatchEvent(new CustomEvent('update-cart', {detail: {openCartDrawer: false}}));
        } catch (err) {
          console.error(err);
        } finally {
          // Always hand the buttons back. A single failed request (a throttled 429, a line
          // item key the cart no longer has) used to leave Remove and the checkout button
          // disabled until the customer reloaded the page
          this.removeButton.disabled = false;
          this.checkoutButton.disabled = false;
        }
      });
    }

    getSellingPlanId(item) {
      return item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : null;
    }
  });
}
